import * as AppleAuthentication from 'expo-apple-authentication';
import * as QueryParams from 'expo-auth-session/build/QueryParams';
import * as Crypto from 'expo-crypto';
import * as WebBrowser from 'expo-web-browser';
import { Platform } from 'react-native';
import type { Session } from '@supabase/supabase-js';
import { getSupabaseClient } from '@/lib/supabase/supabase';
import { getPublicEnvConfigError } from '@/lib/app-public-env';

export const OAUTH_SCHEME = 'controlapagui';
export const OAUTH_CALLBACK_PATH = 'auth/callback';

/** Deep link — cadastrar em Supabase Redirect URLs (ex.: controlapagui://**) */
export const NATIVE_SCHEME_CALLBACK = `${OAUTH_SCHEME}://${OAUTH_CALLBACK_PATH}`;

/** Fallback HTTPS (mesmo da web prod) — só se estiver na allow list do Supabase */
export const PRODUCTION_HTTPS_CALLBACK = 'https://controlapag.com.br/auth/callback';

export type OAuthProvider = 'google' | 'apple';

export type OAuthResult =
  | { status: 'success'; accessToken: string }
  | { status: 'cancelled' }
  | { status: 'no_tokens' }
  | { status: 'error'; message: string };

/** @deprecated use OAuthResult */
export type GoogleOAuthResult = OAuthResult;

let lastOAuthProvider: OAuthProvider = 'google';

export function getLastOAuthProvider(): OAuthProvider {
  return lastOAuthProvider;
}

export function setLastOAuthProvider(provider: OAuthProvider): void {
  lastOAuthProvider = provider;
}

/**
 * Supabase Dashboard → Redirect URLs:
 * - controlapagui://auth/callback (ou controlapagui://**)
 * - https://controlapag.com.br/auth/callback (opcional, via env)
 */
export function getOAuthRedirectUri(): string {
  if (Platform.OS === 'web') {
    return (
      process.env.EXPO_PUBLIC_AUTH_REDIRECT_URL ||
      (typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '')
    );
  }

  const explicit = process.env.EXPO_PUBLIC_MOBILE_OAUTH_REDIRECT_URL?.trim();
  if (explicit) return explicit;

  return NATIVE_SCHEME_CALLBACK;
}

/** @deprecated use getOAuthRedirectUri */
export const getGoogleOAuthRedirectUri = getOAuthRedirectUri;

function logOAuthDev(...args: unknown[]) {
  if (__DEV__) console.log('[OAuth]', ...args);
}

export function parseOAuthCallbackUrl(url: string) {
  return QueryParams.getQueryParams(url);
}

export function isOAuthCallbackUrl(url: string): boolean {
  return (
    url.includes('access_token') ||
    url.includes('code=') ||
    url.includes('error=') ||
    url.includes(OAUTH_CALLBACK_PATH) ||
    url.includes('controlapag.com.br/auth/callback') ||
    url.startsWith(`${OAUTH_SCHEME}://`)
  );
}

let oauthCallbackInFlight = false;

export async function createSessionFromOAuthUrl(url: string): Promise<Session | null> {
  if (!isOAuthCallbackUrl(url)) return null;

  const { params, errorCode } = parseOAuthCallbackUrl(url);
  if (errorCode) {
    throw new Error(errorCode);
  }

  if (params.error_description || params.error) {
    throw new Error(params.error_description || params.error);
  }

  const supabase = getSupabaseClient();
  if (!supabase) {
    throw new Error(getPublicEnvConfigError() ?? 'Supabase não configurado');
  }

  if (params.code && !params.access_token) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(params.code);
    if (error) throw error;
    return data.session;
  }

  const access_token = params.access_token;
  const refresh_token = params.refresh_token;
  if (!access_token) return null;

  const { data, error } = await supabase.auth.setSession({
    access_token,
    refresh_token: refresh_token ?? '',
  });
  if (error) throw error;
  return data.session;
}

export async function processOAuthCallbackUrl(url: string): Promise<Session | null> {
  if (oauthCallbackInFlight) return null;
  oauthCallbackInFlight = true;
  try {
    return await createSessionFromOAuthUrl(url);
  } finally {
    oauthCallbackInFlight = false;
  }
}

export async function signInWithProviderOAuth(provider: OAuthProvider): Promise<OAuthResult> {
  oauthCallbackInFlight = false;
  setLastOAuthProvider(provider);

  if (getPublicEnvConfigError()) {
    return { status: 'error', message: 'Não foi possível entrar. Tente novamente.' };
  }

  const supabase = getSupabaseClient();
  if (!supabase) {
    return { status: 'error', message: 'Não foi possível entrar. Tente novamente.' };
  }

  if (Platform.OS !== 'web') {
    WebBrowser.maybeCompleteAuthSession();
  }

  const redirectUri = getOAuthRedirectUri();
  logOAuthDev('redirectUri:', redirectUri, 'provider:', provider);

  const { data, error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: redirectUri,
      skipBrowserRedirect: Platform.OS !== 'web',
    },
  });

  if (error) {
    logOAuthDev('signInWithOAuth erro:', error.message);
    return { status: 'error', message: 'Não foi possível entrar. Tente novamente.' };
  }

  if (Platform.OS === 'web') {
    if (data?.url && typeof window !== 'undefined') {
      window.location.href = data.url;
    }
    return { status: 'cancelled' };
  }

  if (!data?.url) {
    return {
      status: 'error',
      message: 'Não foi possível entrar. Tente novamente.',
    };
  }

  if (__DEV__) {
    try {
      const parsed = new URL(data.url);
      logOAuthDev('Supabase redirect_to:', parsed.searchParams.get('redirect_to'));
    } catch {
      /* ignore */
    }
  }

  logOAuthDev('abrindo sessão de auth no browser…');

  let result: WebBrowser.WebBrowserAuthSessionResult;
  try {
    result = await WebBrowser.openAuthSessionAsync(data.url, redirectUri, {
      preferEphemeralSession: false,
      showInRecents: false,
    });
  } catch (browserError) {
    logOAuthDev(
      'openAuthSessionAsync erro:',
      browserError instanceof Error ? browserError.message : browserError,
    );
    return { status: 'error', message: 'Falha ao abrir o navegador de login.' };
  }

  logOAuthDev('openAuthSessionAsync type:', result.type);

  if (result.type === 'cancel' || result.type === 'dismiss') {
    return { status: 'cancelled' };
  }

  if (result.type === 'success' && result.url) {
    logOAuthDev('callback URL:', result.url.split('#')[0].split('?')[0]);

    const session = await processOAuthCallbackUrl(result.url);
    if (session?.access_token) {
      return { status: 'success', accessToken: session.access_token };
    }

    logOAuthDev('callback sem tokens — URL pode não estar na allow list do Supabase');
    return { status: 'no_tokens' };
  }

  return { status: 'cancelled' };
}

export async function signInWithGoogleOAuth(): Promise<OAuthResult> {
  return signInWithProviderOAuth('google');
}

/**
 * `authorizationCode` do último login Apple nativo. Vai no corpo de
 * `/auth/apple` e `/auth/oauth/link` para a API trocar por um refresh token e
 * revogá-lo quando a conta for excluída (App Store 5.1.1(v)). Só sai daqui
 * depois que a API aceitar o login: o 404 (cadastro) reenvia o mesmo código.
 */
let pendingAppleAuthorizationCode: string | null = null;

export function getPendingAppleAuthorizationCode(): string | null {
  return pendingAppleAuthorizationCode;
}

export function clearPendingAppleAuthorizationCode(): void {
  pendingAppleAuthorizationCode = null;
}

function isAppleCancelError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { code?: string }).code === 'ERR_REQUEST_CANCELED'
  );
}

/**
 * No iPhone o login Apple é a folha nativa (Guideline 4.8), não o navegador.
 * O `identityToken` vira sessão do Supabase por `signInWithIdToken`, então o
 * resto do fluxo (token do Supabase → `/auth/apple`) é o mesmo da web.
 */
async function signInWithAppleNative(): Promise<OAuthResult> {
  oauthCallbackInFlight = false;
  setLastOAuthProvider('apple');

  if (getPublicEnvConfigError()) {
    return { status: 'error', message: 'Não foi possível entrar. Tente novamente.' };
  }

  const supabase = getSupabaseClient();
  if (!supabase) {
    return { status: 'error', message: 'Não foi possível entrar. Tente novamente.' };
  }

  const rawNonce = Crypto.randomUUID();
  const hashedNonce = await Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, rawNonce);

  let credential: AppleAuthentication.AppleAuthenticationCredential;
  try {
    credential = await AppleAuthentication.signInAsync({
      requestedScopes: [
        AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
        AppleAuthentication.AppleAuthenticationScope.EMAIL,
      ],
      nonce: hashedNonce,
    });
  } catch (error) {
    if (isAppleCancelError(error)) return { status: 'cancelled' };
    logOAuthDev('apple signInAsync erro:', error instanceof Error ? error.message : error);
    return { status: 'error', message: 'Falha no login com Apple.' };
  }

  if (!credential.identityToken) {
    return { status: 'error', message: 'Não foi possível entrar com a Apple. Tente novamente.' };
  }

  const { data, error } = await supabase.auth.signInWithIdToken({
    provider: 'apple',
    token: credential.identityToken,
    nonce: rawNonce,
  });
  if (error) {
    logOAuthDev('apple signInWithIdToken erro:', error.message);
    return { status: 'error', message: 'Não foi possível entrar com a Apple. Tente novamente.' };
  }
  if (!data.session?.access_token) {
    return { status: 'no_tokens' };
  }

  pendingAppleAuthorizationCode = credential.authorizationCode ?? null;

  // A Apple só manda o nome no primeiro login; a API lê `full_name` do Supabase.
  const fullName = [credential.fullName?.givenName, credential.fullName?.familyName]
    .filter(Boolean)
    .join(' ');
  if (fullName) {
    const { error: updateError } = await supabase.auth.updateUser({
      data: { full_name: fullName },
    });
    if (updateError) logOAuthDev('updateUser full_name erro:', updateError.message);
  }

  return { status: 'success', accessToken: data.session.access_token };
}

export async function signInWithAppleOAuth(): Promise<OAuthResult> {
  if (Platform.OS === 'ios') return signInWithAppleNative();
  return signInWithProviderOAuth('apple');
}
